import { useMemo } from "react";
import { useCurrentTimeForAnimation } from "@kaltura/mediaspace-shared-hooks";
import { CncProps, ScheduledMediaChatType } from "./CncProps";

/**
 * Post-processing C&C widget configs on the fly based on dynamic and frontend-only parameters:
 * - Scheduling, according to the current time (e.g. enable the group chat according to the time window)
 * - Values that are passed for a cached page via global variables (e.g. the JWT)
 * - Cookie consent.
 */
export const useProcessedCncConfig = ({
    config,
    isScheduledModeration,
    moderationStartTime,
    moderationEndTime,
    isScheduledGroupChat,
    groupChatStartTime,
    groupChatEndTime,
    isScheduledMediaChat = false,
    scheduledMediaChatConfig,
}: CncProps) => {
    // Refresh the component each second to display live status according to the current time
    const nowSeconds = useCurrentTimeForAnimation(undefined, 1000) / 1000;

    const isTimePassed = (timestamp: number | undefined) => (timestamp == null ? true : nowSeconds >= timestamp);

    // Moderation schedule
    const isPastModerationStartTime = isTimePassed(moderationStartTime);
    const isPastModerationEndTime = isTimePassed(moderationEndTime);
    const isScheduledModerationActive = isPastModerationStartTime && !isPastModerationEndTime;

    // Group chat schedule
    const isPastGroupChatStartTime = isTimePassed(groupChatStartTime);
    const isPastGroupChatEndTime = isTimePassed(groupChatEndTime);
    const isScheduledGroupChatActive = isPastGroupChatStartTime && !isPastGroupChatEndTime;

    // Media chat schedule
    const isPastMediaChatStartTime = isTimePassed(scheduledMediaChatConfig?.startTime);
    const isPastMediaChatEndTime = isTimePassed(scheduledMediaChatConfig?.endTime);

    let scheduledMediaChatType = ScheduledMediaChatType.Disabled;

    if (scheduledMediaChatConfig) {
        // Determine which media-chat mode should be active based on current time
        if (
            // BEFORE session start time (which includes pre-offset)
            !isPastMediaChatStartTime
        ) {
            scheduledMediaChatType = scheduledMediaChatConfig.preScheduleChatType ?? ScheduledMediaChatType.Disabled;
        }
        else if (
            // DURING the scheduled window
            isPastMediaChatStartTime
            && !isPastMediaChatEndTime
        ) {
            scheduledMediaChatType = scheduledMediaChatConfig.duringScheduleChatType ?? ScheduledMediaChatType.Disabled;
        }
        else if (
            // AFTER session end time (which includes post-offset)
            isPastMediaChatEndTime
        ) {
            scheduledMediaChatType = scheduledMediaChatConfig.postScheduleChatType ?? ScheduledMediaChatType.Disabled;
        }
    }

    const jwtToken = useMemo(() => (window as any).KMS_GLOBAL?.cncJwt, []);

    const cookieConsent = useMemo(() => (window as any).GLOBAL_KMS?.shortbread?.getConsentCookie?.()?.functional, []);

    return useMemo(() => {
        let result = config;

        if (isScheduledModeration) {
            // We are on scheduled entry page and entry moderation is configured to According to schedule
            result = {
                ...result,
                moderation: {
                    ...result.moderation,
                    ui: {
                        ...result.moderation?.ui,
                        ctaButton: {
                            ...result.moderation?.ui?.ctaButton,
                            type: isScheduledModerationActive ? "chatWithExpert" : null,
                        },
                    },
                },
            };
        }

        if (isScheduledGroupChat) {
            // We are on scheduled entry page and group chat is configured to "According to schedule"
            result = {
                ...result,
                ui: {
                    ...result.ui,
                    groupChat: {
                        ...result.ui?.groupChat,
                        enabled: isScheduledGroupChatActive,
                    },
                },
            };
        }

        if (isScheduledMediaChat) {
            // We are on scheduled entry page and media chat is configured to "According to schedule"
            result = {
                ...result,
                ui: {
                    ...result.ui,
                    comments: {
                        ...result.ui?.comments,
                        enabled: scheduledMediaChatType !== ScheduledMediaChatType.Disabled,
                        ...(scheduledMediaChatType !== ScheduledMediaChatType.Disabled
                            ? { type: scheduledMediaChatType }
                            : {}),
                    },
                },
            };
        }

        if (jwtToken) {
            result = {
                ...result,
                server: {
                    ...result.server,
                    jwtToken,
                },
            };
        }

        if (cookieConsent) {
            result = {
                ...result,
                consent: {
                    ...result.consent,
                    cookieConsent,
                },
            };
        }

        return result;
    }, [
        config,
        isScheduledModeration,
        isScheduledGroupChat,
        isScheduledMediaChat,
        jwtToken,
        cookieConsent,
        isScheduledModerationActive,
        isScheduledGroupChatActive,
        scheduledMediaChatType,
    ]);
};
